/**
 * Where the developer dock sits, and keeping it somewhere reachable.
 *
 * The dock floats over the gallery screen and is dragged by its handle, so a
 * position is only useful if it survives a reload and can never be parked off
 * the edge — a dock the operator cannot see is a dock they cannot put away.
 */

/** Bottom right, the corner it opens in before anyone drags it. */
export const DEFAULT_DOCK_MARGIN = 24;

/**
 * Clamps a position so the whole dock stays on screen, including when the
 * window has since been made smaller. A preview half off the edge is a
 * preview nobody can read, so none of it is allowed to leave — except when
 * the dock is larger than the screen, where it pins to the top left instead.
 */
export const clampDockPosition = (position, size, viewport) => {
  if (!position) return null;

  const width = size?.width || 0;
  const height = size?.height || 0;
  const screenWidth = viewport?.width || 0;
  const screenHeight = viewport?.height || 0;

  const maxLeft = Math.max(0, screenWidth - width);
  const maxTop = Math.max(0, screenHeight - height);

  return {
    left: Math.min(Math.max(position.left, 0), maxLeft),
    top: Math.min(Math.max(position.top, 0), maxTop),
  };
};

/**
 * The style a position produces. With no position the dock keeps its opening
 * corner, so nothing has to be measured before the first paint.
 */
export const dockAnchorSx = (position) =>
  (position
    ? { left: position.left, top: position.top, right: 'auto', bottom: 'auto' }
    : { right: DEFAULT_DOCK_MARGIN, bottom: DEFAULT_DOCK_MARGIN });
